/**
 * ProviderBreakdownTable Component
 *
 * Displays per-provider compliance statistics for a rule
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';
import {
  Box,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ProviderRuleBreakdown } from '@store/generated/jotaudit';

interface ProviderBreakdownTableProps {
  /** Provider breakdown data */
  byProvider: ProviderRuleBreakdown[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when clicking a provider row */
  onProviderClick?: (userId: string, complianceRate?: number) => void;
}

type SortField = 'provider_name' | 'total_audits' | 'compliance_rate';
type SortDirection = 'asc' | 'desc';

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
};

const getComplianceColor = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined) return '#6B7280'; // Gray
  if (rate >= 80) return '#22C55E'; // Green
  if (rate >= 50) return '#EAB308'; // Yellow
  return '#EF4444'; // Red
};

export const ProviderBreakdownTable: React.FC<ProviderBreakdownTableProps> = ({
  byProvider,
  isLoading = false,
  onProviderClick,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('compliance_rate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const sortedProviders = useMemo(() => {
    const sorted = [...byProvider];
    sorted.sort((a, b) => {
      let aValue: number | string | null | undefined;
      let bValue: number | string | null | undefined;

      switch (sortField) {
        case 'provider_name':
          aValue = a.provider_name || a.user_id;
          bValue = b.provider_name || b.user_id;
          break;
        case 'total_audits':
          aValue = a.total_audits || 0;
          bValue = b.total_audits || 0;
          break;
        case 'compliance_rate':
          aValue = a.compliance_rate ?? null;
          bValue = b.compliance_rate ?? null;
          break;
      }

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = (aValue as number) - (bValue as number);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [byProvider, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleRowClick = useCallback(
    (userId: string, complianceRate?: number) => {
      if (onProviderClick) {
        onProviderClick(userId, complianceRate);
      } else {
        // Default: navigate to provider detail
        navigate(`/jotaudit/provider/${userId}`);
      }
    },
    [navigate, onProviderClick],
  );

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  if (isLoading && byProvider.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total Audits</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Compliant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Warning</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Non-Compliant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Compliance Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 200, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 80, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 60, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 60, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 60, height: 20 }}/>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text"  sx={{ width: 100, height: 20 }}/>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  if (byProvider.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No provider data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: theme.palette.grey[100] },
                }}
                onClick={() => handleSort('provider_name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Provider
                  <SortIcon field="provider_name" />
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: theme.palette.grey[100] },
                }}
                onClick={() => handleSort('total_audits')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Total Audits
                  <SortIcon field="total_audits" />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Compliant</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Warning</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Non-Compliant</TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: theme.palette.grey[100] },
                }}
                onClick={() => handleSort('compliance_rate')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Compliance Rate
                  <SortIcon field="compliance_rate" />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProviders.map(provider => (
              <TableRow
                key={provider.user_id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: theme.palette.grey[50],
                  },
                }}
                onClick={() => handleRowClick(provider.user_id, provider.compliance_rate ?? undefined)}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {provider.provider_name || provider.user_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{provider.total_audits || 0}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                    {provider.compliant_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: theme.palette.warning.main }}>
                    {provider.warning_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                    {provider.non_compliant_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 80 }}>
                      <LinearProgress
                        variant="determinate"
                        value={provider.compliance_rate || 0}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getComplianceColor(provider.compliance_rate),
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'right' }}>
                      {formatPercent(provider.compliance_rate)}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ProviderBreakdownTable;

